import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../i18n';
import { getPostBySlug, getCategoryName, getLatestPosts } from '../data/blogPosts';
import './BlogPost.css';

const BlogPost = () => {
  const { t, language } = useTranslation();
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const business = process.env.REACT_APP_SITE_NAME || 'SeekWebs';

  const localeMap = {
    pt: 'pt-BR',
    en: 'en-US',
    es: 'es-ES'
  };

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = getLatestPosts(4).filter(p => p.slug !== slug).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{post.title} - {business}</title>
        <meta name="description" content={post.description} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:image" content={post.image} />
      </Helmet>

      <article className="blog-post-page">
        <motion.header
          className="post-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/blog" className="back-to-blog">
            <span className="back-arrow">←</span>
            {t('common.backToBlog')}
          </Link>
        </motion.header>

        <motion.div
          className="post-hero"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="post-hero-image">
            <img src={post.image} alt={post.title} />
            <div className="post-hero-overlay" />
          </div>
          <div className="post-hero-content">
            <span className="post-category-badge">{getCategoryName(post.category)}</span>
            <h1 className="post-title">{post.title}</h1>
            <div className="post-meta">
              <span className="post-author">{t('common.by')} {post.author}</span>
              <span className="post-date">{new Date(post.date).toLocaleDateString(localeMap[language] || 'pt-BR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}</span>
              <span className="post-read-time">{post.readTime} {t('common.readTime')}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="post-body"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
        </motion.div>

        <motion.div
          className="post-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <div className="post-share">
            <span>{t('blog.shareLabel')}</span>
            <div className="share-buttons">
              <button
                className="share-btn"
                onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`, '_blank')}
              >
                Twitter
              </button>
              <button
                className="share-btn"
                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
              >
                Facebook
              </button>
              <button
                className="share-btn"
                onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`, '_blank')}
              >
                LinkedIn
              </button>
            </div>
          </div>
        </motion.div>

        {relatedPosts.length > 0 && (
          <motion.section
            className="related-posts"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <h2>{t('blog.relatedPosts')}</h2>
            <div className="related-posts-grid">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="related-post-card"
                >
                  <div className="related-post-image">
                    <img src={relatedPost.image} alt={relatedPost.title} loading="lazy" />
                  </div>
                  <div className="related-post-content">
                    <span className="related-post-category">{getCategoryName(relatedPost.category)}</span>
                    <h3>{relatedPost.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </motion.section>
        )}
      </article>
    </>
  );
};

export default BlogPost;
